/**
 * Phase 18 M1 / M7 / M8 / F21 / F22: client checks that mirror the server (contract.md).
 * Keys are the data paths the server uses in 400 ENTRY_INVALID `fields[]` (e.g. `pain.score`,
 * `teaching.subject`), so server and client messages land on the same inputs. `FORM_ERROR` holds
 * a problem that belongs to the form rather than one field.
 */
import { BLOOD_PRESSURE_PATTERN, VITALS_BOUNDS } from '../../../../dtos/patientmr/charting/vitals.dto';
import type { Medication } from '../../../../dtos/patientmr/medication.dto';
import { NOT_ON_MED_LIST } from '../types';
import type { EntryFormBundle } from './entryForms';
import { THERAPY_CODES } from '../../../../constants/therapyCodes';
import { noteTemplateFor, validateTemplateNote } from './noteTemplates';

/**
 * Phase 18U U8: wide "check this" bands for vitals. Values outside them are allowed (the hard
 * bounds above still apply) and only get an amber note in the form and in the entry list. No
 * age logic: pediatric age-band alerts are backlog (plan §9, R-316 clinical review first).
 */
export const UNUSUAL_VITALS = {
  temperature: { low: 95, high: 104 },
  pulse: { low: 50, high: 200 },
  respiratoryRate: { low: 10, high: 70 },
  o2Saturation: { low: 90, high: 100 },
  systolic: { low: 60, high: 160 },
  diastolic: { low: 30, high: 110 },
} as const;

export const UNUSUAL_NOTE = 'Unusual value. Check before saving.';

const outside = (raw: string | number | null | undefined, band: { low: number; high: number }): boolean => {
  if (raw === null || raw === undefined || String(raw).trim() === '') return false;
  const n = Number(raw);
  return Number.isFinite(n) && (n < band.low || n > band.high);
};

/** Fields of a vitals form (or stored vitals data) outside the "check this" bands */
export function unusualVitals(v: { temperature?: string | number | null; pulse?: string | number | null; respiratoryRate?: string | number | null; o2Saturation?: string | number | null; bloodPressure?: string | null }): string[] {
  const out: string[] = [];
  if (outside(v.temperature, UNUSUAL_VITALS.temperature)) out.push('temperature');
  if (outside(v.pulse, UNUSUAL_VITALS.pulse)) out.push('pulse');
  if (outside(v.respiratoryRate, UNUSUAL_VITALS.respiratoryRate)) out.push('respiratoryRate');
  if (outside(v.o2Saturation, UNUSUAL_VITALS.o2Saturation)) out.push('o2Saturation');
  const [sys, dia] = String(v.bloodPressure || '').split('/');
  if (outside(sys, UNUSUAL_VITALS.systolic) || outside(dia, UNUSUAL_VITALS.diastolic)) out.push('bloodPressure');
  return out;
}

export const REQUIRED = 'Required';
export const FORM_ERROR = '_form';

export type EntryErrors = Record<string, string>;

export interface ValidationContext {
  /** The patient's medications, for "indication is required when the med is PRN" */
  medications?: Medication[];
}

const filled = (v: string | null | undefined): boolean => typeof v === 'string' && v.trim() !== '';
const num = (v: string): number => Number(v.trim());

/** Adds an error when a filled value is not a number in [min, max] */
const checkRange = (errors: EntryErrors, key: string, value: string, min: number, max: number, label?: string, integer = false) => {
  if (!filled(value)) return;
  const n = num(value);
  if (!Number.isFinite(n) || n < min || n > max || (integer && !Number.isInteger(n))) {
    errors[key] = `Range: ${label || `${min}–${max}`}`;
  }
};

const requireField = (errors: EntryErrors, key: string, value: string | null | undefined) => {
  if (!filled(value)) errors[key] = REQUIRED;
};

/** The picked list medication is PRN */
export const isPrnMedication = (medicationId: string, medications: Medication[] = []): boolean =>
  Boolean(medicationId && medicationId !== NOT_ON_MED_LIST && medications.find((m) => m.id === medicationId)?.prn);

export function validateEntryForm(type: string | null | undefined, f: EntryFormBundle, ctx: ValidationContext = {}): EntryErrors {
  const errors: EntryErrors = {};
  switch (type) {
    case 'vitals': {
      const v = f.vitals;
      const bp = filled(v.bloodPressure) ? v.bloodPressure.trim() : '';
      checkRange(errors, 'temperature', v.temperature, VITALS_BOUNDS.temperature.min, VITALS_BOUNDS.temperature.max, VITALS_BOUNDS.temperature.label);
      checkRange(errors, 'pulse', v.pulse, VITALS_BOUNDS.pulse.min, VITALS_BOUNDS.pulse.max, VITALS_BOUNDS.pulse.label);
      checkRange(errors, 'respiratoryRate', v.respiratoryRate, VITALS_BOUNDS.respiratoryRate.min, VITALS_BOUNDS.respiratoryRate.max, VITALS_BOUNDS.respiratoryRate.label);
      checkRange(errors, 'o2Saturation', v.o2Saturation, VITALS_BOUNDS.o2Saturation.min, VITALS_BOUNDS.o2Saturation.max, VITALS_BOUNDS.o2Saturation.label);
      checkRange(errors, 'o2FlowLpm', v.o2FlowLpm, VITALS_BOUNDS.o2FlowLpm.min, VITALS_BOUNDS.o2FlowLpm.max, VITALS_BOUNDS.o2FlowLpm.label);
      checkRange(errors, 'pain.score', v.painScore, VITALS_BOUNDS.painScore.min, VITALS_BOUNDS.painScore.max, VITALS_BOUNDS.painScore.label);
      if (bp && !BLOOD_PRESSURE_PATTERN.test(bp)) errors.bloodPressure = 'Use systolic/diastolic, e.g. 110/70';
      if (filled(v.painScore) && !filled(v.painScale)) errors['pain.scale'] = 'Pick the pain scale';
      if (filled(v.painScale) && !filled(v.painScore)) errors['pain.score'] = REQUIRED;
      const measured = [v.temperature, bp, v.pulse, v.respiratoryRate, v.o2Saturation, v.painScore, v.o2FlowLpm].some(filled);
      if (!measured) errors[FORM_ERROR] = 'Enter at least one measurement.';
      break;
    }
    case 'measurements': {
      const m = f.measurements;
      for (const key of ['height', 'weight', 'headCircumference'] as const) {
        if (filled(m[key]) && !(num(m[key]) > 0)) errors[key] = 'Must be > 0';
      }
      if (![m.height, m.weight, m.headCircumference].some(filled)) errors[FORM_ERROR] = 'Enter at least one measurement.';
      break;
    }
    case 'mar': {
      const m = f.mar;
      if (!filled(m.medicationId)) errors.medicationId = REQUIRED;
      else if (m.medicationId === NOT_ON_MED_LIST) requireField(errors, 'medicationName', m.medicationName);
      requireField(errors, 'administrationTime', m.administrationTime);
      if (filled(m.followUpOf)) {
        // A PRN follow-up records only the effect of the earlier dose
        requireField(errors, 'effectiveness', m.effectiveness);
        break;
      }
      if (m.status !== 'GIVEN') {
        requireField(errors, 'withholdReason', m.withholdReason);
        if (m.withholdReason === 'OTHER' && !filled(m.notes)) errors.notes = 'Describe the reason in the notes.';
      }
      if (isPrnMedication(m.medicationId, ctx.medications)) requireField(errors, 'indication', m.indication);
      break;
    }
    case 'feed': {
      const d = f.feed;
      checkRange(errors, 'volumeMl', d.volumeMl, 0, 5000);
      checkRange(errors, 'durationMinutes', d.durationMinutes, 0, 5000);
      checkRange(errors, 'rateMlPerHour', d.rateMlPerHour, 0, 1000);
      checkRange(errors, 'residualMl', d.residualMl, 0, 1000);
      const any = [d.feedType, d.tolerance, d.formula, d.volumeMl, d.durationMinutes, d.rateMlPerHour, d.residualMl, d.notes].some(filled);
      if (!any) errors[FORM_ERROR] = 'Enter the feed.';
      break;
    }
    case 'notes': {
      const n = f.notes;
      requireField(errors, 'note', n.note);
      if (n.category === 'TEACHING') {
        const t = n.teaching;
        requireField(errors, 'teaching.subject', t.subject);
        requireField(errors, 'teaching.learner', t.learner);
        requireField(errors, 'teaching.method', t.method);
        requireField(errors, 'teaching.understanding', t.understanding);
        if (!filled(t.durationMinutes)) errors['teaching.durationMinutes'] = REQUIRED;
        else checkRange(errors, 'teaching.durationMinutes', t.durationMinutes, 1, 480, '1–480 min', true);
      }
      break;
    }
    case 'wound': {
      const w = f.wound;
      requireField(errors, 'woundDescription', w.woundDescription);
      for (const key of ['length', 'width', 'depth'] as const) {
        if (filled(w[key]) && !(num(w[key]) >= 0)) errors[`woundMeasurements.${key}`] = 'Must be 0 or more';
      }
      break;
    }
    case 'incident':
      requireField(errors, 'description', f.incident.description);
      break;
    case 'summary':
      if (!filled(f.summary)) errors.note = REQUIRED;
      break;
    case 'respiratory': {
      const r = f.respiratory;
      requireField(errors, 'mode', r.mode);
      checkRange(errors, 'fio2', r.fio2, 21, 100, '21–100 %');
      checkRange(errors, 'spo2', r.spo2, 0, 100, '0–100 %');
      checkRange(errors, 'o2FlowLpm', r.o2FlowLpm, 0, 60, '0–60 L/min');
      checkRange(errors, 'rate', r.rate, 0, 120);
      checkRange(errors, 'tidalVolumeMl', r.tidalVolumeMl, 0, 2000);
      checkRange(errors, 'pip', r.pip, 0, 100);
      checkRange(errors, 'peep', r.peep, 0, 40);
      checkRange(errors, 'suctionCount', r.suctionCount, 0, 100, '0–100', true);
      break;
    }
    case 'seizure': {
      const s = f.seizure;
      requireField(errors, 'startedAt', s.startedTime);
      const secs = seizureDurationSeconds(s.durationMinutes, s.durationSeconds);
      if (Number.isNaN(secs) || (secs !== null && (secs < 0 || secs > 7200))) errors.durationSeconds = 'Range: 0–120 min';
      break;
    }
    case 'output': {
      const o = f.output;
      requireField(errors, 'outputType', o.outputType);
      checkRange(errors, 'volumeMl', o.volumeMl, 0, 5000);
      checkRange(errors, 'diaperCount', o.diaperCount, 0, 50, '0–50', true);
      break;
    }
    case 'therapy': {
      // Phase 20C: mirrors entryValidation.therapyTreatmentRules on the server
      const t = f.therapy;
      const kindOf = (code: string) => THERAPY_CODES.find((c) => c.code === code)?.kind;
      const seen = new Set<string>();
      let lines = 0;
      t.lines.forEach((l, i) => {
        if (!filled(l.code)) return;
        lines += 1;
        if (seen.has(l.code)) errors[`lines[${i}].code`] = 'Listed twice; record its minutes once';
        seen.add(l.code);
        if (kindOf(l.code) === 'TIMED' && !filled(l.minutes)) errors[`lines[${i}].minutes`] = REQUIRED;
        checkRange(errors, `lines[${i}].minutes`, l.minutes, 1, 240, '1–240', true);
      });
      if (!filled(t.evaluationCode) && lines === 0) errors.lines = 'Record the evaluation code or at least one procedure';
      t.goalsAddressed.forEach((g, i) => {
        if (filled(g.goal)) requireField(errors, `goalsAddressed[${i}].progress`, g.progress);
      });
      if (t.coTreatment) requireField(errors, 'coTreatmentWith', t.coTreatmentWith);
      break;
    }
    case 'aidetasks': {
      // Phase 20D: mirrors entryValidation.aideTasksRules on the server
      const a = f.aidetasks;
      const seen = new Set<string>();
      let rows = 0;
      a.tasks.forEach((t, i) => {
        if (!filled(t.task)) return;
        rows += 1;
        const key = t.task.trim().toLowerCase();
        if (seen.has(key)) errors[`tasks[${i}].task`] = 'Listed twice';
        seen.add(key);
        requireField(errors, `tasks[${i}].status`, t.status);
        if (t.status === 'REFUSED') requireField(errors, `tasks[${i}].note`, t.note);
        checkRange(errors, `tasks[${i}].minutes`, t.minutes, 1, 480, '1–480', true);
      });
      if (rows === 0) errors.tasks = 'Record at least one task';
      break;
    }
    case 'supervisory': {
      const s = f.supervisory;
      requireField(errors, 'supervisedUserId', s.supervisedUserId);
      requireField(errors, 'supervisedDiscipline', s.supervisedDiscipline);
      requireField(errors, 'staffPresent', s.staffPresent);
      requireField(errors, 'pocFollowed', s.pocFollowed);
      if (filled(s.nextDueDate) && !/^\d{4}-\d{2}-\d{2}$/.test(s.nextDueDate)) errors.nextDueDate = 'Use a date';
      break;
    }
    default: {
      // Phase 20E / 20F: note templates (social work, respiratory, nutrition, behavioral, symptom)
      const template = noteTemplateFor(type);
      if (template) Object.assign(errors, validateTemplateNote(template, f.templatenote));
      break;
    }
  }
  return errors;
}

/** Minutes + seconds -> seconds; null when both are blank, NaN when not numbers */
export function seizureDurationSeconds(minutes: string, seconds: string): number | null {
  if (!filled(minutes) && !filled(seconds)) return null;
  const m = filled(minutes) ? num(minutes) : 0;
  const s = filled(seconds) ? num(seconds) : 0;
  if (!Number.isFinite(m) || !Number.isFinite(s) || m < 0 || s < 0) return NaN;
  return Math.round(m * 60 + s);
}

/** True when nothing blocks saving */
export const isEntryValid = (errors: EntryErrors): boolean => Object.keys(errors).length === 0;
